import { useQuery, UseQueryResult } from "react-query";
import { useDebounce } from "@cinesys/ui/hooks";
import CustomerApiService from "../../../CustomerApiService";

/**
 * `spotter` mapping-page API calls (iconik-customer-portal spec §8.6, M4). Mirrors the
 * `ab-vault/candidate-assignments/service.ts` convention: thin wrappers over
 * `CustomerApiService.fetch`, one function per endpoint, bare-JSON contract (no envelope).
 *
 * All paths are prefixed `/spotter` (mounted by `apps/customer-api/src/routes/index.ts`,
 * gated by `requirePlugin("spotter-mapping")`).
 *
 * v2.0.0 (round 8) — destination-first rework matching `docs/cinesys-nfl/portal-mapping-mock_v2.html`
 * (round 15: this working doc is now gitignored, local-only, not present in a fresh clone/CI)
 * and `apps/customer-api/src/services/customerSpotterMapping.ts` (read that file's doc comment for the full
 * v1 -> v2 story). Types here are snake_case, matching the wire contract verbatim — this is now
 * the ONE shape used end to end (backend storage, this API, and what n8n itself reads).
 */

const PREFIX = "/spotter";

/** A flat string is a field's whole sample value; a structured object additionally carries one
 *  entry per `ContractField.formatVars` token (e.g. `PenaltyTeam`'s `type`/`team`), so Formatting
 *  mode's `{token}` substitutions have something real to fill in — see `FieldsScreen.tsx`'s
 *  `sampleVarsFor`. Mirrors `SpotterSampleValue` in
 *  `apps/customer-api/src/routes/spotter/contract.ts` — that's also where the actual sample DATA
 *  now lives: part of the uploaded contract document itself (each field's own `sample`
 *  property), authored by whoever maintains that file's content, not merged in server-side from
 *  a separate hardcoded table. */
export type SpotterSampleValue =
  | string
  | { value: string; [formatVar: string]: string };

/** One field in the Spotter output contract (uploaded as a JSON blob via the admin plugin-config
 *  screen's generic JSON-slot card — see `apps/customer-api/src/services/jsonSlots.ts`). Mirrors
 *  `SpotterContractField` in `apps/customer-api/src/routes/spotter/contract.ts` — see that file's
 *  doc comment for the v1 -> v2 schema story (dropped `_Auto-Tag` suffix, server-provided
 *  `label`, `translation` replaces `source`/`pbpColumn`, new `scope`). */
export interface ContractField {
  key: string;
  label: string;
  type: string;
  group: string;
  translation: "direct" | "matching" | "formatting" | "glossary" | "none";
  paths: string[];
  description: string;
  scope: "asset" | "segment" | "asset+segment";
  valueEnum?: string | string[];
  formatVars?: string[];
  formatExample?: string;
  /** Set when this field's values are the customer's OWN glossary terms (returned verbatim, no
   *  Matching needed) — the value is the glossary field name (e.g. "Descriptors"). */
  glossaryField?: string;
  /** Illustrative sample value for the mapping page's Tag preview card — part of the uploaded
   *  contract document, server-provided verbatim (see `contract.ts`'s doc comment), so every
   *  consumer of the contract (not just this frontend) sees the same sample data, authored in
   *  exactly one place. Optional: a field with no `sample` in the uploaded document just shows
   *  no preview value. */
  sample?: SpotterSampleValue;
}

/** `GET /contract` response — the full parsed contract (never hardcoded client-side). */
export interface Contract {
  contract: string;
  version: string;
  description: string;
  fields: ContractField[];
}

/** One field within a customer's iconik metadata view. */
export interface IconikMetadataField {
  name: string;
  label: string;
  field_type: string;
}

/** `GET /iconik-views` response entry — the customer's OWN live iconik metadata view.
 *  `scope` (added alongside segment-view support) distinguishes whether this view is written on
 *  the whole clip (`"asset"`) or on a per-play segment inside a full-broadcast asset
 *  (`"segment"`) — see `ContractField.scope` above (same three values). */
export interface IconikMetadataView {
  id: string;
  name: string;
  description: string;
  view_fields: IconikMetadataField[];
  scope: "asset" | "segment";
}

export type MappingMode = "direct" | "match" | "format" | "conditional";

/** One `mode === "conditional"` rule — evaluated in order, first match wins. Mirrors
 *  `ConditionalRule` in `apps/customer-api/src/services/customerSpotterMapping.ts` by hand (this app doesn't
 *  bundle `xlucene-parser` for the browser — see `spotterConditionEval.ts`'s doc comment — so
 *  there's no shared types package to import it from). An empty/whitespace `when` always matches,
 *  letting a rule act as a fallback default under first-match-wins. */
export interface ConditionalRule {
  when: string;
  then: {
    field: string;
    set: string;
    mode: "append" | "overwrite";
  };
}

/** One destination-field mapping row — the customer's iconik field is the key; one or more
 *  Spotter source fields feed it, translated per `mode`. */
export interface MappingEntry {
  from: string | string[];
  mode: MappingMode;
  to_view_id: string;
  to_field_id: string;
  /** Which metadata surface `to_view_id` belongs to — mirrors the destination view's own `scope`
   *  (see `IconikMetadataView`). Sourced from the same `GET /iconik-views` response the view
   *  picker itself is built from, same trust level as `to_view_id`/`to_field_id`. */
  scope: "asset" | "segment";
  /** `mode === "match"` only. */
  value_map?: Record<string, string>;
  /** `mode === "format"` only. */
  template?: string;
  /** Set only when `mode === "direct"` and `from` has 2+ sources. */
  merge?: "append_unique";
  /** `mode === "conditional"` only — `from` is server-derived (the union of every field
   *  referenced across these rules' `when` strings) rather than client-submitted. */
  rules?: ConditionalRule[];
  /** `true` when this entry's `to_view_id` no longer matches either of the org's currently
   *  configured mapping views — the backend (`markStaleEntries`,
   *  `apps/customer-api/src/services/customerSpotterMapping.ts`) sets this instead of silently dropping the
   *  entry, so it can still be shown with a warning. Absent (not `false`) on a valid entry. */
  stale?: boolean;
}

/** `GET /mapping` response — `contract_version` is `null` when nothing has been saved yet. */
export interface StoredMapping {
  contract_version: string | null;
  fields: MappingEntry[];
}

/** GET `/contract` — the mapping page's Spotter source-field catalog. */
export async function getContract(): Promise<Contract> {
  return CustomerApiService.fetch<Contract>("GET", `${PREFIX}/contract`);
}

/** GET `/iconik-views` — the mapping page's destination-field source (the caller's own token). */
export async function getIconikViews(): Promise<IconikMetadataView[]> {
  return CustomerApiService.fetch<IconikMetadataView[]>(
    "GET",
    `${PREFIX}/iconik-views`
  );
}

/** `GET /iconik-views/comment-destination` response — a fixed descriptor, not a live-per-org
 *  read (see that route's own doc comment). Kept server-driven (one source of truth for the id
 *  every comment entry is keyed by — `../fields/commentDestination.ts`'s sentinel constants are
 *  purely local UI bookkeeping, not this) rather than a frontend-only constant. */
export interface CommentDestination {
  id: string;
}

/** GET `/iconik-views/comment-destination` — the always-available "map to an iconik comment"
 *  destination (Seth Tanner, Monday 12926430722). Unlike `getIconikViews` above, this never
 *  varies by org/live iconik state. */
export async function getCommentDestination(): Promise<CommentDestination> {
  return CustomerApiService.fetch<CommentDestination>(
    "GET",
    `${PREFIX}/iconik-views/comment-destination`
  );
}

/** GET `/mapping` — the org's existing stored mapping, if any. */
export async function getMapping(): Promise<StoredMapping> {
  return CustomerApiService.fetch<StoredMapping>("GET", `${PREFIX}/mapping`);
}

/** PUT `/mapping` — persist the full current mapping. Returns the (server-validated) saved shape. */
export async function saveMapping(body: {
  contract_version: string;
  fields: MappingEntry[];
}): Promise<StoredMapping> {
  return CustomerApiService.fetch<StoredMapping>(
    "PUT",
    `${PREFIX}/mapping`,
    body
  );
}

/** Bare string, not this file's other array-of-keys convention — react-query hashes a bare
 *  string and a single-element array to different cache entries, and `useMapping` below is this
 *  key's only caller. */
export const MAPPING_QUERY_KEY = "spotter-mapping";

/** Named-hook wrapper around `getMapping`, following this app's own "named hook, not an inline
 *  `useQuery` call" convention (see `apps/customer-frontend/CLAUDE.md`'s "Data fetching"
 *  section). `RenameScreen.tsx` is the only caller — it cross-references the Field Mapping store
 *  to filter its own token chips down to mapped fields only (`filterToMappedFields`); the Fields
 *  tab reads its own separate `spotter-fields` store instead (`fields/service.ts`'s
 *  `useFields`), not this one. */
export function useMapping(): UseQueryResult<StoredMapping, Error> {
  return useQuery<StoredMapping, Error>(MAPPING_QUERY_KEY, getMapping);
}

/** `POST /mapping/preview-condition` response — a syntax-only check (does `when` parse, and only
 *  use node types the compiler can turn into n8n's structured condition tree), not a
 *  match-against-data check: a condition referencing multiple fields will almost never match the
 *  org's single illustrative sample record, so that would-be signal was mostly noise. */
export type ConditionSyntaxResult =
  | { valid: true }
  | { valid: false; error: string };

async function checkConditionSyntax(
  when: string
): Promise<ConditionSyntaxResult> {
  return CustomerApiService.fetch<ConditionSyntaxResult>(
    "POST",
    `${PREFIX}/mapping/preview-condition`,
    { when }
  );
}

export interface ConditionSyntaxCheck {
  data: ConditionSyntaxResult | undefined;
  /** True from the moment `when` diverges from the last-checked value (still waiting out the
   *  debounce) through the in-flight request itself — `keepPreviousData` otherwise leaves
   *  `data` holding the PRIOR condition's result across both of those spans, which reads as "this
   *  is still valid" for a condition that hasn't actually been checked yet. */
  isChecking: boolean;
  isError: boolean;
}

/** Live syntax check for a Conditional-mode rule's `when` string, debounced so a network call
 *  doesn't fire on every keystroke (`xlucene-parser` can't bundle for the browser, so this can't
 *  be done client-side). */
export function useConditionSyntaxCheck(when: string): ConditionSyntaxCheck {
  const debouncedWhen = useDebounce(when, 350);
  const query = useQuery<ConditionSyntaxResult, Error>(
    ["spotter-condition-syntax-check", debouncedWhen],
    () => checkConditionSyntax(debouncedWhen),
    { keepPreviousData: true }
  );
  return {
    data: query.data,
    isChecking: when !== debouncedWhen || query.isFetching,
    isError: query.isError,
  };
}
